/**
 * 选人场景：8 角色 4×2 格子，方向键移动光标，右侧显示角色详情。
 * Z 确认 → gameScene；X 返回标题。
 */
import { VIEW_W, VIEW_H } from '../config.js';
import { Input } from '../engine/input.js';
import { cycleIndex } from '../engine/menu.js';
import { CHARACTERS } from '../content/characters.js';
import { getDifficulty } from '../content/difficulties.js';
import { t, charName, getLang, L, diffName } from '../i18n.js';
import { wrapText } from '../engine/wrapText.js';

const COLS = 4;
const CELL_W = 72;
const CELL_H = 84;
const GRID_X = 16;
const GRID_Y = 90;


export const CharSelectScene = {
  mgr: null,

  enter(params) {
    this.idx = 0;
    this.t = 0;
    // 难度由 difficultySelect 注入；直接进入时回落 Normal；Extra 模式带 mode='extra'
    const pd = params && params.difficulty;
    this.difficulty = getDifficulty(pd && (pd.id || pd));
    this.mode = (params && params.mode) || 'normal';
    this.practice = (params && params.practice) || null;
  },

  exit() {},

  update(dt) {
    this.t += dt;
    const audio = this.mgr.shared.audio;
    let moved = false;
    if (Input.pressed('left')) { this.idx = cycleIndex(this.idx, CHARACTERS.length, -1); moved = true; }
    if (Input.pressed('right')) { this.idx = cycleIndex(this.idx, CHARACTERS.length, 1); moved = true; }
    if (Input.pressed('up')) { this.idx = cycleIndex(this.idx, CHARACTERS.length, -COLS); moved = true; }
    if (Input.pressed('down')) { this.idx = cycleIndex(this.idx, CHARACTERS.length, COLS); moved = true; }
    if (moved) audio.playSfx('cursor');
    if (Input.pressed('confirm')) {
      audio.playSfx('select');
      this.mgr.switchTo('game', { character: CHARACTERS[this.idx], difficulty: this.difficulty, mode: this.mode, practice: this.practice });
    }
    if (Input.pressed('cancel')) {
      audio.playSfx('cursor');
      this.mgr.switchTo(this.mode === 'extra' ? 'title' : this.mode === 'practice' ? 'spellPractice' : 'difficultySelect');
    }
  },

  draw(r) {
    const assets = this.mgr.shared.assets;
    r.text(t('char.title'), VIEW_W / 2, 30, { size: 22, color: '#fff', align: 'center' });
    r.text(`${this.mode === 'practice' ? t('practice.title') + ' · ' : ''}${t('char.diffLabel')}${diffName(this.difficulty)}`, VIEW_W / 2, 56, { size: 12, color: '#fc3', align: 'center' });
    r.text(t('char.hint'), VIEW_W / 2, 74, { size: 11, color: '#889', align: 'center' });

    // 角色格子
    for (let i = 0; i < CHARACTERS.length; i++) {
      const c = CHARACTERS[i];
      const cx = GRID_X + (i % COLS) * CELL_W;
      const cy = GRID_Y + Math.floor(i / COLS) * CELL_H;
      const sel = i === this.idx;
      r.rect(cx, cy, CELL_W - 8, CELL_H - 8, {
        fill: sel ? '#224' : '#112',
        stroke: sel ? '#ff4' : '#334',
      });
      const img = assets.img(c.sprite);
      if (img) {
        const sc = 44 / img.width;
        r.sprite(img, cx + (CELL_W - 8) / 2, cy + 28, { scale: sc });
      }
      // 名字按语言：英文 romaji（name），中文 cnName
      r.text(charName(c), cx + (CELL_W - 8) / 2, cy + CELL_H - 26, { size: 11, color: sel ? '#ff4' : '#aab', align: 'center' });
    }

    // 右侧角色卡：头像 + 名字（与左侧格子严格同高对齐，不压提示文字）
    const c = CHARACTERS[this.idx];
    const px = 312;
    r.rect(px, GRID_Y, VIEW_W - px - 16, 2 * CELL_H - 8, { fill: '#112', stroke: '#334' });
    const img = assets.img(c.sprite);
    if (img) {
      const sc = 60 / img.width;
      r.sprite(img, px + (VIEW_W - px - 16) / 2, GRID_Y + 56, { scale: sc });
    }
    const displayName = getLang() === 'zh' ? `${c.cnName}（${c.name}）` : c.name;
    r.text(displayName, px + (VIEW_W - px - 16) / 2, GRID_Y + 108, { size: 13, color: c.color, align: 'center' });

    // 底部通栏详情：简介（换行 ≤2 行）+ 三件套（名称行 + 说明缩进行；英文较长故按行数撑高面板）
    const py = GRID_Y + 2 * CELL_H + 16;
    let ty = py + 12;
    const drawLines = (text, x, maxWidth, size, color, lineH, maxLines) => {
      const lines = wrapText(text, maxWidth, size).slice(0, maxLines);
      for (const line of lines) {
        r.text(line, x, ty, { size, color });
        ty += lineH;
      }
    };
    // 先量后画：求面板高度再统一绘制（desc/三件套说明经 L() 按语言取）
    const measure = (text, maxWidth, size, maxLines) => Math.min(wrapText(text, maxWidth, size).length, maxLines);
    let h = 12 + measure(L(c.desc), VIEW_W - 60, 11, 2) * 16 + 8 + 24; // +24 性能行
    const kits = [
      [t('char.shot'), L(c.shot.name), L(c.shot.desc)],
      [t('char.bomb'), L(c.bomb.name), L(c.bomb.desc)],
      [t('char.special'), L(c.special.name), L(c.special.desc)],
    ];
    for (const kit of kits) h += 18 + measure(kit[2], VIEW_W - 110, 11, 2) * 15 + 6;
    r.rect(16, py, VIEW_W - 32, h + 4, { fill: '#112', stroke: '#334' });
    drawLines(L(c.desc), 28, VIEW_W - 60, 11, '#99a', 16, 2);
    ty += 8;
    // 基础性能行（相对基准 4.5/1.8/64/2.5；速度/磁吸越大越好，判定越小越好）
    {
      const st = c.stats || {};
      const statDefs = [
        [t('char.statFast'), st.speedFast || 4.5, 4.5, 1],
        [t('char.statSlow'), st.speedSlow || 1.8, 1.8, 1],
        [t('char.statMagnet'), st.magnet || 64, 64, 1],
        [t('char.statHitbox'), st.hitbox || 2.5, 2.5, -1],
      ];
      r.text(t('char.stats'), 28, ty, { size: 12, color: '#8cf' });
      let sx = 86;
      for (const [label, v, base, dir] of statDefs) {
        const better = dir * (v - base) > 0;
        const worse = dir * (v - base) < 0;
        const mark = better ? '▲' : worse ? '▼' : '·';
        const color = better ? '#8f8' : worse ? '#f88' : '#aab';
        r.text(`${label} ${v}${mark}`, sx, ty, { size: 11, color });
        sx += 100;
      }
      ty += 24;
    }
    for (const [label, name, desc] of kits) {
      r.text(label, 28, ty, { size: 12, color: '#8cf' });
      r.text(name, 86, ty, { size: 12, color: '#ccd' });
      ty += 18;
      drawLines(desc, 86, VIEW_W - 110, 11, '#99a', 15, 2);
      ty += 6;
    }
  },
};
